import { Divider, Typography } from '@material-ui/core';
import { FormikCheckboxField, useFormikEnhanced } from '@superdispatch/forms';
import { Column, Columns, Stack, useSnackbarStack } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { Form, FormikProvider } from 'formik';
import { useMemo } from 'react';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import { useSettleInstantTransfer } from 'shared/modules/superpay/SuperPayAPI';
import { InstantTransferSettleDialogDTO } from 'shared/modules/superpay/SuperPayDTO';
import styled from 'styled-components';
import { isWebView } from 'shared/constants/AppTypeConstants';

const CheckboxGroup = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionWrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
`;

function getSelectedGUIDs(values: Record<string, boolean>) {
  return Object.keys(values).filter(
    (key) => key !== 'isTermsAgree' && values[key],
  );
}

interface InstantTransferMultipleSettleDialogContentProps {
  onClose: () => void;
  onSuccess: () => void;
  settleInformation?: InstantTransferSettleDialogDTO;
  title?: string;
}

export function InstantTransferMultipleSettleDialogContent({
  onClose,
  onSuccess,
  settleInformation,
  title = 'Settle Instant Transfer Advance',
}: InstantTransferMultipleSettleDialogContentProps) {
  const { addSnackbar } = useSnackbarStack();
  const { mutateAsync: payNow } = useSettleInstantTransfer();
  const loads = settleInformation?.loads;
  const isAccessRestricted =
    settleInformation?.status === 'past_due' ||
    settleInformation?.status === 'failed';

  const initialValues = useMemo(() => {
    const values: Record<string, boolean> = { isTermsAgree: false };
    loads?.forEach(({ load_guid }) => {
      values[load_guid] = true;
    });
    return values;
  }, [loads]);

  const formik = useFormikEnhanced({
    initialValues,
    onSubmit: (values) => payNow(getSelectedGUIDs(values)),
    onSubmitSuccess: () => {
      addSnackbar('Settlement Funds Submitted successfully.', {
        variant: 'success',
      });
      onSuccess();
      onClose();
    },
    onSubmitFailure: () => {
      addSnackbar('Something went wrong. Please try again.', {
        variant: 'error',
      });
    },
  });

  const hasSelection = useMemo(
    () => getSelectedGUIDs(formik.values).length > 0,
    [formik.values],
  );

  const totalAmount = useMemo(() => {
    const selectedGUIDs = getSelectedGUIDs(formik.values);
    return selectedGUIDs.reduce((sum, guid) => {
      const load = loads?.find((l) => l.load_guid === guid);
      return sum + (load?.price ?? 0);
    }, 0);
  }, [formik.values, loads]);

  const formattedTotal = formatCurrency(totalAmount, {
    minimumFractionDigits: 0,
  });

  return (
    <Box width={['100%', '100%', '600px']}>
      <FormikProvider value={formik}>
        <Form>
          <Box padding="medium">
            <Stack space="small">
              <Stack space="small">
                <Typography variant="h3">{title}</Typography>
                <Stack>
                  {isAccessRestricted && (
                    <Typography color="error">
                      Your Instant Transfer access has been restricted due to an
                      outstanding payments.
                    </Typography>
                  )}
                  <Typography>
                    Settle the payment for {loads?.length} loads to regain
                    access to Instant Transfer.{' '}
                    <b>We'll debit the bank account listed below.</b>
                  </Typography>
                </Stack>
              </Stack>

              <Box
                borderRadius="small"
                backgroundColor="Silver200"
                padding="small"
              >
                <Stack space="small">
                  <CheckboxGroup>
                    {loads?.map((load) => (
                      <Columns
                        align={['top', 'center']}
                        key={load.load_guid}
                        collapseBelow="tablet"
                        space={['xsmall', 'none']}
                      >
                        <Column width="2/5">
                          <FormikCheckboxField
                            name={load.load_guid}
                            label={
                              <Typography variant="body1">
                                {load.load_id}
                              </Typography>
                            }
                          />
                        </Column>
                        <Column>
                          <Typography>
                            {formatCurrency(load.price, {
                              minimumFractionDigits: 0,
                            })}
                          </Typography>
                        </Column>
                      </Columns>
                    ))}
                  </CheckboxGroup>

                  <Divider />

                  <Columns align="center">
                    <Column width="2/5">
                      <Stack space="small">
                        <Typography variant="body1">Amount</Typography>
                        <Typography variant="body1">Bank Details</Typography>
                      </Stack>
                    </Column>
                    <Column>
                      <Stack space="small">
                        <Typography>{formattedTotal}</Typography>
                        <Typography>
                          {settleInformation?.bank_account}
                        </Typography>
                      </Stack>
                    </Column>
                  </Columns>
                </Stack>
              </Box>

              <FormikCheckboxField
                name="isTermsAgree"
                label={
                  <Typography>
                    I confirm sufficient funds are available, I understand that
                    in case of payment issue access to Instant Transfer program
                    may be lost.
                  </Typography>
                }
              />

              <ActionWrapper>
                <Button
                  fullWidth={isWebView}
                  variant="neutral"
                  onClick={onClose}
                  disabled={formik.isSubmitting}
                >
                  No, I'll Pay Later
                </Button>
                <Button
                  fullWidth={isWebView}
                  type="submit"
                  disabled={
                    !formik.values.isTermsAgree ||
                    !hasSelection ||
                    formik.isSubmitting
                  }
                >
                  Pay Now
                </Button>
              </ActionWrapper>
            </Stack>
          </Box>
        </Form>
      </FormikProvider>
    </Box>
  );
}
